import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import './SessionTimeoutWarning.css';

/**
 * SessionTimeoutWarning Component
 * 
 * Displays a warning modal when the user's session is about to expire.
 * Shows a countdown timer and options to stay logged in or logout.
 * 
 * SECURITY FEATURES:
 * - Modal is non-dismissible (backdrop="static", keyboard={false})
 * - Clear call-to-action for security awareness
 * - Countdown provides urgency
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onStayLoggedIn - Called when user clicks "Stay Logged In"
 * @param {Function} props.onLogout - Called when user clicks "Logout"
 * @param {number} props.secondsRemaining - Seconds until automatic logout
 */
const SessionTimeoutWarning = ({ 
    isOpen, 
    onStayLoggedIn, 
    onLogout, 
    secondsRemaining 
}) => {
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const isUrgent = secondsRemaining <= 30;

    return (
        <Modal 
            show={isOpen} 
            onHide={onStayLoggedIn}
            backdrop="static" 
            keyboard={false} 
            centered
            size="md"
            dialogClassName="stw-dialog"
        >
            <Modal.Header className="stw-header">
                <Modal.Title className="stw-title">
                    <span role="img" aria-label="warning">&#9888;&#65039;</span>
                    Session Expiring Soon
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="stw-body">
                <div className="stw-content">
                    <p className="stw-message">
                        Your session will expire due to inactivity.
                    </p>
                    
                    <div className={`stw-timer ${isUrgent ? 'stw-timer--urgent' : ''}`}>
                        {formatTime(secondsRemaining)}
                    </div>
                    
                    <p className="stw-subtext">
                        For your security, you will be automatically logged out when the timer reaches zero.
                    </p>
                </div>
            </Modal.Body>
            
            <Modal.Footer className="stw-footer">
                <Button 
                    variant="outline-secondary" 
                    onClick={onLogout}
                    className="stw-btn stw-btn--logout"
                >
                    Logout Now
                </Button>
                <Button 
                    variant="success" 
                    onClick={onStayLoggedIn}
                    className="stw-btn stw-btn--stay"
                >
                    Stay Logged In
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SessionTimeoutWarning;
